import React, { useEffect, useRef, useState } from "react";
import { X, Megaphone, Mail, CheckCircle, XCircle, Clock } from "lucide-react";
import Skeleton from "components/ui/Skeleton";
import NoDataState from "modules/swiftboard/components/NoDataState";
import apiCampaign from "../api";
import { CAMPAIGN_STATUS_STYLE, CAMPAIGN_STATUS_LABEL } from "../features/campaign.constants";

const RECIPIENT_STATUS_ICON = {
  SENT: { icon: CheckCircle, className: "text-success" },
  DELIVERED: { icon: CheckCircle, className: "text-success" },
  READ: { icon: CheckCircle, className: "text-success" },
  FAILED: { icon: XCircle, className: "text-danger" },
  PENDING: { icon: Clock, className: "text-text_muted" },
  SCHEDULED: { icon: Clock, className: "text-text_muted" },
};

const CampaignReport = ({ campaignId, onClose }) => {
  const overlayRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;
    setLoading(true);
    apiCampaign
      .getReport(campaignId, { limit: 100, skip: 0 })
      .then((res) => {
        if (!cancelled) setReport(res.data?.data || null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [campaignId]);

  if (!campaignId) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const campaign = report?.campaign;
  const s = campaign ? CAMPAIGN_STATUS_STYLE[campaign.status] || CAMPAIGN_STATUS_STYLE.DRAFT : null;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 bg-black/60 z-[1999] flex items-center justify-center p-4"
    >
      <div className="bg-surface w-full max-w-2xl rounded-xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden border border-border">
        <div className="flex items-start justify-between px-5 py-4 border-b border-border bg-primary rounded-t-xl flex-shrink-0">
          <div className="space-y-1 min-w-0">
            <div className="flex items-center gap-2">
              <Megaphone size={16} className="text-white/80 flex-shrink-0" />
              <h2 className="text-sm font-semibold text-white truncate">{campaign?.campaign_name || "Campaign Report"}</h2>
            </div>
            {campaign?.subject && (
              <div className="flex items-center gap-1.5 text-white/60 text-xs">
                <Mail size={11} className="flex-shrink-0" />
                <span className="truncate">{campaign.subject}</span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-white/70 mt-0.5 flex-shrink-0">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="p-5 space-y-3">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton.Rows rows={5} />
          </div>
        ) : !campaign ? (
          <NoDataState icon="mail" title="Report not found" showRefresh={false} showBack={false} size="small" />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-border bg-background flex-shrink-0">
              <span className={`inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-medium border ${s.bg} ${s.text} ${s.border}`}>
                {CAMPAIGN_STATUS_LABEL[campaign.status] || campaign.status}
              </span>
              <span className="text-xs text-text_secondary">
                {campaign.total_sent}/{campaign.total_recipients} sent
              </span>
              <span className="text-xs text-text_secondary">· {campaign.total_opened} opened</span>
              <span className="text-xs text-text_secondary">· {campaign.total_bounced} failed</span>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-border">
              {(report.recipients || []).map((r) => {
                const cfg = RECIPIENT_STATUS_ICON[r.status] || RECIPIENT_STATUS_ICON.PENDING;
                const Icon = cfg.icon;
                return (
                  <div key={r.id} className="flex items-center gap-3 px-5 py-2.5">
                    <Icon size={14} className={`flex-shrink-0 ${cfg.className}`} />
                    <span className="text-xs text-text_primary truncate flex-1">{r.email}</span>
                    <span className="text-[11px] text-text_muted flex-shrink-0">{r.status}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CampaignReport;
